<template>
	<div id="click-wrapper" @click="onClick">
		<ErrorBoundary>
			<BotsNotAllowed v-if="IS_BOT" />
			<WebAccessNotAllowed
				v-else-if="
					!IS_DEV &&
					!FLAG_ENABLE_WEB &&
					!socketService.hasFlutterToken
				"
			/>
			<div
				v-else-if="!isMaintenanceMode"
				@touchstart="onFirstClick"
				@click="onFirstClick"
			>
				<TapToContinue
					:loadingDone="loadingDone"
					v-show="!firstClick"
				/>

				<DataLoader>
					<RouterView />

					<template v-if="firstClick">
						<RouterView name="header" />
						<RouterView name="footer" />
						<RouterView name="leftbox" />
						<RouterView name="rightbox" />
						<PopupComponent :visible="undefined" />
						<TutorialFocusLayer />
					</template>
				</DataLoader>
			</div>
			<MaintenanceMode v-else />
		</ErrorBoundary>
	</div>
</template>

<script lang="ts" setup>
import BotsNotAllowed from "@/components/BotsNotAllowed.vue";
import DataLoader from "@/components/DataLoader.vue";
import ErrorBoundary from "@/components/ErrorBoundary.vue";
import LevelUp from "@/components/LevelUp.vue";
import MaintenanceMode from "@/components/MaintenanceMode.vue";
import OfflinePopup from "@/components/Offline/OfflinePopup.vue";
import PopupComponent from "@/components/Popup.vue";
import TapToContinue from "@/components/TapToContinue.vue";
import TutorialFocusLayer from "@/components/TutorialFocusLayer.vue";
import WebAccessNotAllowed from "@/components/WebAccessNotAllowed.vue";
import {
	FLAG_ENABLE_WEB,
	GAME_TICKER_INTERVAL,
	IS_BOT,
	IS_DEV,
} from "@/config/constants";
import { catchUpEventEnd } from "@/event";
import { ResourceType } from "@/game/entities/config/resources";
import useSocketService, { EmitStores } from "@/hooks/useSocketService";
import Communicator from "@/libs/AppCommunicatior";
import {
	PopupDictionary,
	PopupType,
	usePopupStore,
} from "@/store/usePopupStore";
import useQuestStore from "@/store/useQuestStore";
import useResourcesStore from "@/store/useResourcesStore";
import useRuntimeStore from "@/store/useRuntimeStore";
import useUserStore from "@/store/useUserStore";
import { type Timestamp } from "@/types";
import { useDebounceFn } from "@vueuse/core";
import { hideAllPoppers } from "floating-vue";
import { storeToRefs } from "pinia";
import { markRaw, onMounted, onUnmounted, ref, watch } from "vue";
import { RouterView, useRouter } from "vue-router";
import ConnectionErrorPopup from "./components/Offline/ConnectionErrorPopup.vue";
import InactivityPopup from "./components/Offline/InactivityPopup.vue";
import SoundService from "./game/SoundService";
import useEmitter, { EmitterEvents } from "./hooks/useEmitter";
import useGameTicker from "./hooks/useGameTicker";
import { SkinTypes } from "./types/ui";
import { type DeepLinkData, processDeepLink } from "./utils/deepLinkHandler.js";
import { floorNumber } from "./utils/number";
import { setupQuestAudioPreloading } from "./utils/questAudioPreloader";
import { translate } from "./utils/translate";

const router = useRouter();

const ticker = markRaw(useGameTicker());
const emitter = markRaw(useEmitter());
const socketService = markRaw(useSocketService());
const soundService = markRaw(SoundService.getInstance());
const communicator = markRaw(Communicator.getInstance());
const popupStore = usePopupStore();
const questsStore = useQuestStore();
const userStore = useUserStore();

const runtimeStore = useRuntimeStore();
const { firstClick, isMaintenanceMode, pendingDeepLink } =
	storeToRefs(runtimeStore);

const resourcesStore = useResourcesStore();

const CLICKS_PER_SECOND = 15;
const MAINTENANCE_POLL_INTERVAL = 10_000;

const loadingDone = ref<boolean>(false);

/**
 * Processes a deep link if the game is ready to show popups.
 * Called by the watch below when `pendingDeepLink`, `loadingDone`, or `firstClick` changes.
 * If the game isn't ready yet, the deep link is left in the store for the next state change.
 */
const processPendingDeepLink = (deepLink: DeepLinkData | null): void => {
	// Only process if the game has finished loading and the user has clicked
	if (!loadingDone.value || !firstClick.value || !deepLink) return;

	// Clear the pending deep link immediately to prevent duplicate processing
	runtimeStore.setPendingDeepLink(null);

	// Dispatch to the deep link handler (handles all types including referral-data)
	processDeepLink(deepLink);
};

/**
 * Watches for deep link data set by Flutter via `sendDeepLinkData`.
 *
 * When `pendingDeepLink` becomes non-null:
 *   **Pre-offline links** (`welcome-back`, `welcome-back-reward`): processed immediately.
 *   **Post-offline links** (`welcome-back-offer`, `event`, `referral`, `stats`,
 *    `upgrades`, `market`, `settings`): the deep link handler returns
 *    callback that App.vue attaches to the offline income popup, so the
 *    deep link action fires after the offline popup is dismissed.
 *
 * The watch also re-evaluates when `loadingDone` or `firstClick` changes,
 * so a deep link set while the game is loading will fire once the game is ready.
 *
 * After processing, `pendingDeepLink` is cleared to `null` so the next deep link
 * is detected on the next Flutter call.
 */
watch(
	(): { link: DeepLinkData | null; loaded: boolean; clicked: boolean } => ({
		link: pendingDeepLink.value,
		loaded: loadingDone.value,
		clicked: firstClick.value,
	}),
	(): void => {
		processPendingDeepLink(pendingDeepLink.value);
	},
	{ immediate: false },
);

let clicks = new Array<Timestamp>();
let stopAudioPreloading: (() => void) | null = null;
let maintenancePollInterval: ReturnType<typeof setInterval> | null = null;
let maintenancePollInFlight = false;
let hadMaintenanceMode = false;

const onClick = (): void => {
	resourcesStore.increase(ResourceType.CLICKS_TOTAL, 1, false);

	clicks = clicks.filter(
		(click: Timestamp): boolean => Date.now() - click < 1_000,
	);
	clicks.push(Date.now());

	if (clicks.length > CLICKS_PER_SECOND) {
		// eslint-disable-next-line no-console
		console.log("Clicking too fast! Reporting to admins...");

		// TODO: implement reporting logic
	}
};

ticker.stop();

const onFirstClick = (): void => {
	if (!loadingDone.value || firstClick.value) {
		return;
	}

	runtimeStore.setFirstClick(true);

	communicator.firstTimeClicked();
	soundService.firstClickMusic();
	emitter.emit(EmitterEvents.GameInitStoryMode);
	if (!ticker.isLocked) ticker.start();

	// First run when the game loads
	emitter.emit(EmitterEvents.GameShowOfflinePopup, false);
};

const onGameLoadingDone = (): void => {
	loadingDone.value = true;
};

const stopMaintenancePolling = (): void => {
	if (!maintenancePollInterval) return;
	clearInterval(maintenancePollInterval);
	maintenancePollInterval = null;
};

const pollMaintenanceState = async (): Promise<void> => {
	if (maintenancePollInFlight) return;
	maintenancePollInFlight = true;

	try {
		await socketService.emitStateRequest();
	} catch (error: unknown) {
		console.error("Failed to poll maintenance state:", error);
	} finally {
		maintenancePollInFlight = false;
	}
};

const startMaintenancePolling = (): void => {
	if (maintenancePollInterval) return;

	void pollMaintenanceState();
	maintenancePollInterval = setInterval((): void => {
		void pollMaintenanceState();
	}, MAINTENANCE_POLL_INTERVAL);
};

// New logic for system blockers
const handleInactivity = (): void => {
	ticker.stop();
	runtimeStore.setIsInactivityPopupVisible(true);

	const randomImgVersion = floorNumber(Math.random() * 5) + 1;

	popupStore.showPopup({
		id: PopupDictionary.INACTIVITY,
		type: PopupType.INFO,
		isPriority: true, // Immediately overwrite everything on screen
		dropOnInterrupt: true,
		maxZIndex: true, // It will be at the very top level
		decoratedHeading: translate("ui.offline_mode"),
		maxHeight: 25,
		width: 23.875,
		percentageWidth: "95%",
		isPaddingless: true,
		skin: SkinTypes.LIGHT,
		imgSrc: `offline/offline-earnings-${randomImgVersion}.avif`,
		closeCallback: undefined, // Cannot be closed with the close button
		component: markRaw(InactivityPopup),
	});
};

const handleConnectionError = (): void => {
	ticker.stop();
	runtimeStore.setIsInactivityPopupVisible(true);

	popupStore.showPopup({
		id: PopupDictionary.CONNECTION_ERROR,
		type: PopupType.INFO,
		isPriority: true, // Critical
		dropOnInterrupt: true,
		maxZIndex: true, // Top level
		decoratedHeading: translate("ui.connection_lost"),
		maxHeight: 25,
		width: 23.875,
		percentageWidth: "95%",
		isPaddingless: true,
		skin: SkinTypes.LIGHT,
		isCentered: true,
		closeCallback: undefined, // Must not be closable
		component: markRaw(ConnectionErrorPopup),
	});
};

watch(
	(): boolean => isMaintenanceMode.value,
	(maintenanceMode: boolean): void => {
		if (maintenanceMode) {
			hadMaintenanceMode = true;
			ticker.stop();
			startMaintenancePolling();
			return;
		}

		stopMaintenancePolling();

		if (hadMaintenanceMode) {
			window.location.reload();
			return;
		}

		if (firstClick.value && !ticker.isStarted && !ticker.isLocked) {
			ticker.start();
		}
	},
	{ immediate: true },
);

const handleScroll = useDebounceFn((): void => {
	hideAllPoppers();
}, 100);

const onPageVisible = (): void => {
	if (document.visibilityState !== "visible") return;
	catchUpEventEnd();
};

const handleGameShowOfflinePopup = (refreshStore: boolean): void => {
	// Skip offline popup during tutorial
	if (questsStore.tutorialActive) return;

	(refreshStore
		? socketService.emitLoadRequest({ stores: [EmitStores.USER] })
		: Promise.resolve()
	)
		.then(async (): Promise<void> => {
			emitter.emit(EmitterEvents.GameRecalculateOfflineRewards);

			// Check if any time has passed at all
			if (userStore.lastTickBefore < GAME_TICKER_INTERVAL) return;

			const rewards = await socketService.getClaimableRewards();
			const claimableRewards = rewards?.claimableRewards;

			if (claimableRewards && Object.keys(claimableRewards).length > 0) {
				window.purchaseFinished("", "true", claimableRewards);
			}

			popupStore.showPopup({
				id: PopupDictionary.OFFLINE_EARNINGS,
				decoratedHeading: translate("ui.offline_earnings"),
				component: OfflinePopup,
				uniqueById: true,
				isPaddingless: true,
				skin: SkinTypes.LIGHT,
				width: 23.875,
				maxHeight: 37,
				spriteData: {
					slug: "offline-earnings",
					key: floorNumber(Math.random() * 5) + 1,
					size: 378,
				},
			});
		})
		.catch((error: unknown): void => {
			console.error("Failed to show offline popup:", error);
		});
};

const handleToggleLevelUpPopup = (value: boolean): void => {
	if (value) {
		popupStore.showPopup({
			id: PopupDictionary.LEVEL_UP,
			type: PopupType.INFO,
			width: 24,
			height: "auto",
			isPaddingless: true,
			skin: SkinTypes.TRANSPARENT,
			scaleDelay: 400,
			component: markRaw(LevelUp),
		});
	}
};

onMounted((): void => {
	emitter.on(EmitterEvents.GameLoadingDone, onGameLoadingDone);
	catchUpEventEnd();
	document.addEventListener("visibilitychange", onPageVisible);

	// Offer Claimable Rewards > Offline Income > Daily Bonus > News > Event
	emitter.on(EmitterEvents.GameShowOfflinePopup, handleGameShowOfflinePopup);

	emitter.on(EmitterEvents.ToggleLevelUpPopup, handleToggleLevelUpPopup);
	emitter.on(EmitterEvents.GameShowInactivePopup, handleInactivity);
	emitter.on(
		EmitterEvents.GameShowConnectionErrorPopup,
		handleConnectionError,
	);

	// Setup audio preloading for intro and quest-specific audio
	// This works because it's called within the component's setup context
	const questStore = useQuestStore();
	const { activeQuests, isIntroActive } = storeToRefs(questStore);
	stopAudioPreloading = setupQuestAudioPreloading(
		activeQuests,
		isIntroActive,
	);
	window.addEventListener("scroll", handleScroll, true);
	router.push("/");
});

onUnmounted((): void => {
	emitter.off(EmitterEvents.GameLoadingDone, onGameLoadingDone);
	emitter.off(EmitterEvents.GameShowOfflinePopup, handleGameShowOfflinePopup);
	emitter.off(EmitterEvents.ToggleLevelUpPopup, handleToggleLevelUpPopup);
	emitter.off(EmitterEvents.GameShowInactivePopup, handleInactivity);
	emitter.off(
		EmitterEvents.GameShowConnectionErrorPopup,
		handleConnectionError,
	);
	window.removeEventListener("scroll", handleScroll, true);
	document.removeEventListener("visibilitychange", onPageVisible);
	stopMaintenancePolling();
	// Clean up audio preloading watchers
	if (stopAudioPreloading) {
		stopAudioPreloading();
	}
});
</script>
